import type * as AM5 from '@amcharts/amcharts5'
import type * as AM5XY from '@amcharts/amcharts5/xy'
import type am5themes_Animated_t from '@amcharts/amcharts5/themes/Animated'
import type am5themes_Responsive_t from '@amcharts/amcharts5/themes/Responsive'
import type * as am5plugins_exporting_t from '@amcharts/amcharts5/plugins/exporting'
import * as bounds from 'binary-search-bounds'
import chroma from 'chroma-js';
import type { ITimeInterval } from '@amcharts/amcharts5/.internal/core/util/Time'
import { format_duration } from '@/time'
import { debouncer, kmToMile, meterToFoot, throttle, throttleTimeout } from './utils'
import { type ActivityAxisType, type ActivityRecordsData, type XAxisData, type Signature, type StravaSegmentInfo, type XertSegment, noopMapController, type MapController, type LatLngData, iconDiamondOrange, iconDiamond, type MapControllerInterface, type SharedActivity, type PowerType, type SharedActivityMarked, type SharedActivityOutput, type SharedActivityRecord } from '@/Common/constants'
import { getLibxert } from '@/xert-wasm'
import { get, type Writable } from 'svelte/store'

export type am5xyChart = AM5XY.XYChart
export type am5DateAxisWithAxisRenderer = AM5XY.DateAxis<AM5XY.AxisRenderer>
export type am5DataItemWithIDateAxisDataItem = AM5.DataItem<AM5XY.IDateAxisDataItem>
export type am5AxisRendererY = AM5XY.AxisRendererY
export type am5CircleTemplate = AM5.Template<AM5.Circle>
export type am5Root = AM5.Root
export type am5Tooltip = AM5.Tooltip

/**
 * Helper to extract am5 modules from window and add types to them. Needs the module to be loaded beforehand to work
 */
export function getTypedAm5() {
    //@ts-expect-error
    const am5: typeof AM5 = window.am5
    //@ts-expect-error
    const am5xy: typeof AM5XY = window.am5xy
    //@ts-expect-error
    const am5themes_Animated: typeof am5themes_Animated_t = window.am5themes_Animated
    //@ts-expect-error
    const am5themes_Responsive: typeof am5themes_Responsive_t = window.am5themes_Responsive
    //@ts-expect-error
    const am5plugins_exporting: typeof am5plugins_exporting_t = window.am5plugins_exporting

    return {am5, am5xy, am5themes_Animated, am5themes_Responsive, am5plugins_exporting}
}

export const powerGradient = chroma.scale(['bbbbbb', '699cfe', '50e3c2', '79ff4c', 'ffef4c', 'ff7d2a', '#ff0c00'])
    
const timeGridIntervals: ITimeInterval[]  = [
    { timeUnit: 'second', count: 1 },
    { timeUnit: 'second', count: 2 },
    { timeUnit: 'second', count: 5 },
    { timeUnit: 'second', count: 10 },
    { timeUnit: 'second', count: 15 },
    { timeUnit: 'second', count: 30 },
    { timeUnit: 'minute', count: 1 },
    { timeUnit: 'minute', count: 2 },
    { timeUnit: 'minute', count: 5 },
    { timeUnit: 'minute', count: 10 },
    { timeUnit: 'minute', count: 15 },
    { timeUnit: 'minute', count: 30 },
    { timeUnit: 'hour', count: 1 },
    { timeUnit: 'hour', count: 2 },
    { timeUnit: 'hour', count: 6 },
    { timeUnit: 'hour', count: 12 },
    { timeUnit: 'day', count: 1 },
];

const distGridIntervals: ITimeInterval[] = [
    { timeUnit: 'raw', count: 10000 },
    { timeUnit: 'raw', count: 25000 },
    { timeUnit: 'raw', count: 50000 },
    { timeUnit: 'raw', count: 100000 },
    { timeUnit: 'raw', count: 250000 },
    { timeUnit: 'raw', count: 500000 },
    { timeUnit: 'raw', count: 1000000},
    { timeUnit: 'raw', count: 2000000 },
    { timeUnit: 'raw', count: 5000000 },
    { timeUnit: 'raw', count: 10000000 },
    { timeUnit: 'raw', count: 25000000 },
    { timeUnit: 'raw', count: 50000000 },
];

const axisConfig = {
    strokeOpacity: 1,
    strokeWidth: 2,
    minGridDistance: 40,
};

const axisGridConfig = {
    disabled: true,
    visible: false,
};

export const activityChartPalette = {
    'cadence': '#50E3C2',
    'heartRate': '#A50013',
    'difficulty': '#000000',
    'altitude': '#000000',
    'targetPower': '#353535',
    'speed': '#7ED321',
    'overageEfforts': '#FFFF00',
    // 'thresholdPower': '#DAA520'
    'thresholdPower': '#FF0000'
}

interface XertChartRoot {
    root: am5Root,
    chart: am5xyChart,
}

interface XertAxis extends XertChartRoot {
    startVisible ?: boolean
}

interface XertAxisSeries extends XertChartRoot {
    dateAxis: MyDateAxis,
    startingAxisType: ActivityAxisType,
    startVisible?: boolean
}

interface XertSeries extends XertAxisSeries {
    axis: AM5XY.ValueAxis<AM5XY.AxisRenderer>
}

export declare class MyDateAxis extends AM5XY.DateAxis<any> {
    oldGroupSeries(series: AM5XY.LineSeries): void;
    _groupData(): void;
    _groupSeriesData(series: AM5XY.LineSeries): void;
    getSeriesItem(series: AM5XY.LineSeries, position: number): any;
    getSeriesItemByValue(series: AM5XY.LineSeries, value: number): any;
}

/**
 * Module for creating different components of the original Activity Chart. 
 * Contains custom code for grouping data and creating the date axis (time/distance).
 * Am charts must be loaded before calling this function. 
*/
export function getXertAm5() {

    const {am5, am5xy, am5themes_Animated, am5plugins_exporting} = getTypedAm5()

    function setDataUngrouped(data: any) {
        this.data.setAll(data);

        let multiples = [2, 5, 10, 15, 30, 60, 120, 180, 240];
        let baseInterval = { timeUnit: 'raw', count: 1000};
        for (let multiple of multiples) {
        let interval = {timeUnit: baseInterval.timeUnit, count: baseInterval.count*multiple};
        let dataSetId = interval.timeUnit + interval.count;
        this._dataSets[dataSetId] = this._mainDataItems;
        }

        this.markDirtyValues();
        this._markDirtyGroup();
        this._dataChanged = true;
        this._afterDataChange();
        this._prepareChildren(); 
    }

    class MyLineSeries extends am5xy.LineSeries {
        setDataUngrouped = setDataUngrouped
    }

    class MySmoothedXLineSeries extends am5xy.SmoothedXLineSeries {
        setDataUngrouped = setDataUngrouped
    }

    class MyTheme extends am5themes_Animated {
        setupDefaultRules() {
            super.setupDefaultRules();
            
            this.rule('Button', ['zoom']).setAll({
                x: null,
                y: null,
                marginLeft: 10
            });

            // TODO: This is the Kelly theme colorset, maybe import the actually theme and use that instead ??
            this.rule("ColorSet").setAll({
                colors: [
                    am5.color(0xF3C300), am5.color(0x875692), am5.color(0xF38400),
                    am5.color(0xA1CAF1), am5.color(0xBE0032), am5.color(0xC2B078),
                    am5.color(0x848482), am5.color(0x008856), am5.color(0xE68FAC),
                    am5.color(0x0067A5), am5.color(0xF99379), am5.color(0x604E97),
                    am5.color(0xF6A600), am5.color(0xB3446C), am5.color(0xDCD300),
                    am5.color(0x882D17), am5.color(0x8DB600), am5.color(0x654522),
                    am5.color(0xE25822), am5.color(0x2B3D26)
                ],
                step: 1
            });
    
            let rule = this.rule('Graphics', ['segment-close', 'icon', 'button', 'zoom']);
            rule.setAll({
                strokeOpacity: 0.7,
                draw: function (display) {
                    display.moveTo(0, 6);
                    display.lineTo(12, -6);
                    display.moveTo(0, -6);
                    display.lineTo(12, 6);
                }
            });
        }
    }
    
    /**
     * Re-implementation of am5.DataItem. The original is slow when creating 10,000s of items.
    */
    class MyDataSettings {
        valueX: any
        valueXWorking: any
        valueY: any
        valueYWorking: any
        visible: boolean
    
        constructor(x, y) {
            this.valueX = x;
            this.valueXWorking = x;
            this.valueY = y;
            this.valueYWorking = y;
            this.visible = true;
        }
    }

    class MyDataItem {
        dataContext: any
        component: any
        visible: boolean
        _settings: MyDataSettings
    
        constructor(component, data, x, y) {
            this.dataContext = data;
            this.component = component;
            this.visible = true;
            this._settings = new MyDataSettings(x, y);
        }
    
        get(key, fallback = null) {
            return this._settings[key] ?? fallback;
        }
        set(key, val) {
            this._settings[key] = val;
        }
        setRaw(key, val) {
            this._settings[key] = val;
        }
        isHidden() {
            return false;
        }
        markDirty(){
            this.component.markDirtyValues(this);
        }
        dispose() { /* Do nothing */ }
    }
    
    function prepareDistData(data: {mm: number}[]) {
        let dataDist = [];
        let mmPrev = -1;
        data.forEach((v)=>{
            if (v.mm > mmPrev) {
                mmPrev = v.mm;
                dataDist.push({...v});
            }
        });
        return dataDist;
    }
    
    /**
     * This is a custom function for setting the data of the chart series.
     */
    let fastGroupData = (chartSeries, data, axisType: ActivityAxisType, xAxes = null, minMax = false, calcBase = true, _multiples = null) => {
    
        const dataDist = prepareDistData(data);

        const isTime = axisType === "time"
    
        const groupedDataSets = {};
        //let chartSeries = [...chart.series.values, powerFillSeriesClone];
        // Explicitly assign which series to group.
        // All other series should have series.data.setAll(data) called elsewhere.
        for (let series of chartSeries) {  
            series._mainDataItems.length = 0;
            series._onDataClear();
            groupedDataSets[series.uid] = { raw1000: series._mainDataItems };
        }
    
        let baseInterval = { timeUnit: 'raw', count: 1000};
        let intervals = [baseInterval];
        let multiples = _multiples ?? [2, 5, 10, 15, 30, 60, 120, 180, 240];
        // let multiples = [2, 5, 10, 15, 30, 120];
        // let multiples = [2, 15, 60];
        let dataForPoints = isTime ? data : dataDist;
        let n = dataForPoints.length;
        for (let multiple of multiples) {
            let interval = {timeUnit: baseInterval.timeUnit, count: baseInterval.count*multiple};
            intervals.push(interval);
            let dataSetId = interval.timeUnit + interval.count;
            // let newData = [];
    
            let m = Math.ceil(n/multiple);
            for (let series of chartSeries) {
                // groupedDataSets[series.uid][dataSetId] = Array.from({length: m}, ()=>{
                //     return new am5.DataItem(series, null, {});
                // });
                groupedDataSets[series.uid][dataSetId] = [];
            }
        }
    
        // Keep track of max power, etc values per interval.
        // that way data peak shape is preserved when data grouped.
        let maxs = {};
        let maxDatas = {};
        let avgs = {};
        let nAvgs = {};
        let xNexts = {};
        for (let multiple of multiples) {
            maxs[multiple] = {};
            maxDatas[multiple] = {};
            avgs[multiple] = {};
            nAvgs[multiple] = {};
            for (let series of chartSeries) {
                let seriesId = series.uid;
                    avgs[multiple][seriesId] = 0;
                    nAvgs[multiple][seriesId] = 0;
            }
            xNexts[multiple] = 0;
        }
        for (let i = 0; i < dataForPoints.length; ++i) {
            const p0 = dataForPoints[i];
            const lastPoint = i == dataForPoints.length - 1;
            let x = isTime ? p0.unix_time : p0.mm;
    
            for (let series of chartSeries) {
                let valueY = series.get('valueYField');
                let y = p0[valueY];
                groupedDataSets[series.uid]['raw1000'].push(
                    new MyDataItem(series, p0, x, y)
                );
                // if (minMax)
                for (let multiple of multiples) {
                    let seriesId = series.uid;
                    let valueY = series.get('valueYField');
                    let y = p0[valueY];
    
                    let compare = valueY == 'mpa' ? (a, b) => b > a : (a, b) => a > b;
                    let max = maxs[multiple][seriesId];
                    if (max == null || compare(y, max)) {
                        maxs[multiple][seriesId] = y;
                        maxDatas[multiple][seriesId] = p0;
                    }
                    if (y > 0) {
    
                        avgs[multiple][seriesId] += y;
                        nAvgs[multiple][seriesId] += 1;
                    }
                }
            }
    
            // Store max and avg point for better shape preservation when zooming out.
            // if (minMax)
            for (let multiple of multiples) {
                // if (j % multiple == 0) {
                if (x >= xNexts[multiple]) {
                    // Step by 2x multiple, since 2 data points are generated ata a time (max and avg)
                    let dx = 2*multiple*1000;
                    let x0 = xNexts[multiple] - dx;
                    xNexts[multiple] += dx;
    
                    for (let series of chartSeries) {
                        //let y = Math.max(p0._settings["valueY"], p1._settings["valueY"]);
                        let valueY = series.get('valueYField');
                        //let y = Math.max(p0[valueY], p1[valueY]);
                        let y = p0[valueY];
    
                        let seriesId = series.uid;
    
                        let max = maxs[multiple][seriesId];
                        let maxData = maxDatas[multiple][seriesId] ?? p0;
    
                        let nAvg = nAvgs[multiple][seriesId];
                        let avg = nAvg > 1 ? (avgs[multiple][seriesId] - max) / (nAvg-1) : y;
                        let dataSetId = 'raw'+(multiple*1000);
    
                        let xMax = isTime ? maxData.unix_time : maxData.mm;
    
                        let xAvg = (x0 + x)/2;
    
    
                        let maxItem = new MyDataItem(series, maxData, xMax, max);
    
                        if (nAvg > 1) { // max > 0 && (max - avg) / max > 0.2) {
    
                            let avgItem = new MyDataItem(series, p0, xAvg, avg);
                            if (xMax <= xAvg) {
                                groupedDataSets[seriesId][dataSetId].push(maxItem);
                            }
                            if (xMax !== xAvg) {
                                groupedDataSets[seriesId][dataSetId].push(avgItem);
                            }
                            if (xMax > xAvg) {
                                groupedDataSets[seriesId][dataSetId].push(maxItem);
                            }
                        } else {
                            groupedDataSets[seriesId][dataSetId].push(maxItem);
                        }
    
    
    
    
                        avgs[multiple][seriesId] = 0;
                        nAvgs[multiple][seriesId] = 0;
                        maxs[multiple][seriesId] = null;
                        maxDatas[multiple][seriesId] = null;
                    }
                }
                // Insert last data point into every grouped series for the purpose of selection bounds.
                if (lastPoint) {
                    for (let series of chartSeries) {
                        let valueY = series.get('valueYField');
                        let seriesId = series.uid;
                        let dataSetId = 'raw'+(multiple*1000);
                        let y = p0[valueY];
                        groupedDataSets[seriesId][dataSetId].push(new MyDataItem(series, p0, x, y));
                    }
                }
            }
            //  else for (let multiple of multiples) {
            //     // Faster grouping that just downsamples at a fixed rate.
            //     if (x >= xNexts[multiple]) {
            //         let dx = multiple*1000;
            //         // let x0 = xNexts[multiple] - dx;
            //         xNexts[multiple] += dx;
    
            //         for (let series of chartSeries) {
            //             let valueY = series.get('valueYField');
            //             let y = p0[valueY];
    
            //             let seriesId = series.uid;
    
            //             let dataSetId = 'raw'+(multiple*1000);
    
            //             groupedDataSets[seriesId][dataSetId].push(MyDataItem(series, p0, {
            //                 valueX: x,
            //                 valueXWorking: x,
            //                 valueY: y,
            //                 valueYWorking: y,
            //             }));
            //         }
            //     }
            // }
        }
    
        for (let series of chartSeries) {
            if (calcBase) series._dataSets = groupedDataSets[series.uid];
            else for (let multiple of multiples) {
                let seriesId = series.uid;
                let dataSetId = 'raw'+(multiple*1000);
                series._dataSets[dataSetId] = groupedDataSets[seriesId][dataSetId];
            }
        }
    
        if (xAxes) for (const axis of xAxes) {
            axis._dataGrouped = true; // helps to avoid double grouping
            axis._seriesDataGrouped = true;
            axis.set('groupIntervals', intervals);
            axis.set('baseInterval', baseInterval);
        }
    
        for (let series of chartSeries) {
            // series._mainDataItems = series._dataSets["raw1000"];
            series.markDirtyValues();
            series._markDirtyGroup();
            series._dataChanged = true;
            // series.notifyDataChanged(data);
            series._afterDataChange();
            series._prepareChildren();
        }
    
        return {
            data, dataDist
        }
    }
    
    function createMyDateAxis({
        root,
        chart,
        startingAxisType,
        distanceUnits,
        handleRangeChange,
        groupData = true,
    }: XertChartRoot & {
        startingAxisType: ActivityAxisType,
        distanceUnits: string,
        handleRangeChange: (selectedMin: number, selectedMax: number, axisType: ActivityAxisType) => void,
        groupData?: boolean,
    }) {
    
        let isTime = startingAxisType === "time"
        let selectionBounds = {min: 0, max: 0}
        let mainDataSeries: AM5XY.LineSeries
        /**
         * Function that sets the data for the chart.
         */
        let validateData: (axisType: ActivityAxisType) => void
        const debounce = debouncer(100)

        function getValidateData() {
            if(validateData) return validateData
            throw new Error("Please set validateData with setValidateData().")
        }
        function setValidateData(fn: typeof validateData) {
            validateData = fn
        }
        
        function getMainDataSeries() {
            if(mainDataSeries) return mainDataSeries
            throw new Error("Please set mainDataSeries with setMainDataSeries().")
        }
        function setMainDataSeries(series: AM5XY.LineSeries) {
            mainDataSeries = series
        }        

        function getAxisType(): ActivityAxisType {
            return isTime ? "time" : "distance"
        }
    
        class MyDateAxis extends am5xy.DateAxis<any> {
        
            // Fallback to original data grouping for series that don't get processed in customValidateData.
            oldGroupSeries(series) {
                super._groupSeriesData(series);
            }
        
            _groupData() { /* Do nothing */ }
        
            _groupSeriesData(series) { /* Do nothing */ }
        
            getSeriesItem(series, position) {
                const value = this.positionToValue(position);
                return this.getSeriesItemByValue(series, value);
            }
        
            getSeriesItemByValue(series, value) {
                let data = series._mainDataItems;
                if (data.length == 0) return null;
        
                let cmp = (a, b) => {
                    return a._settings.valueX - b._settings.valueX;
                }
        
                const arg = {
                    _settings: {
                        valueX: value
                    }
                }
        
                let i = bounds.le(data, arg, cmp);
                if (i >= data.length ) i = data.length - 1;
                if (i < 0) i = 0;
                
                // return data[i];
        
                let item = data[i];
                let interval = this.getPrivate('groupInterval');
                let diff = value - item._settings.valueX;
                if (diff < 0) diff = -diff;
                // const max_diff = interval ? interval.count * 2 : (isTime ? 5000 : 15000);
                const max_diff = (isTime ? 2500 : 25000);
                return diff < max_diff ? item : null;
            }

        }
    
        let dateAxisRenderer = am5xy.AxisRendererX.new(root, {
            strokeOpacity: 1,
            strokeWidth: 2,
        });
        let dateAxis = chart.xAxes.push(MyDateAxis.new(root, {
            maxDeviation: 0, // Prevent zoom out beyond min/max
            groupData,
            groupCount: groupData ? 900 : 1E6,
            snapTooltip: false, // Don't snap to nearest grid interval
    
            tooltipIntervalOffset: 0,
    
            baseInterval: { timeUnit: 'raw', count: 1000 },
            gridIntervals: timeGridIntervals,
    
            renderer: dateAxisRenderer,
            tooltipDateFormat: 'ss',
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis'],
            })
        }));
        dateAxisRenderer.grid.template.setAll(axisGridConfig);
        dateAxisRenderer.labels.template.setAll({
            minPosition: 0.05,
            location: 0.0,
            multiLocation: 0.0,
        });

        dateAxisRenderer.set('minGridDistance', 60)
        dateAxisRenderer.axisFills.template.set('disabled', true)
        dateAxisRenderer.ticks.template.set('disabled', true)
    
        class MyDateFormatter extends am5.DateFormatter {
            format(source, format) {
                let value = source.getTime();
                if (isTime) {
                    return format_duration(value / 1000);
                } else {
                    return Number((value / 1000000).toFixed(2)) + ' ' + distanceUnits;
                }
            }
        }
        
        root.dateFormatter = MyDateFormatter.new(root, {});

        async function toggleX(prevData: XAxisData[]) {
            isTime = !isTime;
            const axisType = isTime ? "time" : "distance"
            let series = [...chart.series.values];
            if (isTime) {
                series.forEach((s)=>{
                    s.set('valueXField', 'unix_time');
                });
    
                dateAxis.set('gridIntervals',  timeGridIntervals);
            } else {
                series.forEach((s)=>{
                    s.set('valueXField', 'mm');
                });
    
                dateAxis.set('gridIntervals', distGridIntervals);
            }

            const startData = prevData[selectionBounds.min]
            const endData = prevData[selectionBounds.max]

            getValidateData()(axisType) // Gets and calls validate data
            const zoomStart = isTime ?  startData.unix_time : startData.mm;
            const zoomEnd = isTime ?  endData.unix_time : endData.mm;

            // Need to wait for the axis to update after validate data.
            await chartReady(root, 100, () => {
                dateAxis.zoomToValues(zoomStart, zoomEnd, 200)
            })
            
            return axisType
        }

        function onZoom () {
            const series = getMainDataSeries();
                
            const data = series._mainDataItems;
            if (data.length == 0) return;
            const cmp = (a, b)=>{
                return a._settings.valueX - b._settings.valueX;
            };
            let i0 = bounds.ge(data, {_settings: {valueX: dateAxis.getPrivate('selectionMinFinal')}}, cmp);
            let i1 = bounds.le(data, {_settings: {valueX: dateAxis.getPrivate('selectionMaxFinal')}}, cmp, i0);
            selectionBounds.min = Math.max(0, i0);
            selectionBounds.max = Math.min(data.length - 1, i1);

            // Since this can be called once for selectionMinFinal and once for selectionMaxFinal, ensure it only runs once.
            debounce.debounce(() => handleRangeChange(selectionBounds.min, selectionBounds.max, isTime ? 'time' : 'distance'));
        }
        dateAxis.onPrivate('selectionMinFinal', onZoom)
        dateAxis.onPrivate('selectionMaxFinal', onZoom)

        // TODO: This can be moved out
        function createScrollbar({root, chart, scrollBarFillYField = "power", visible = true}: XertChartRoot & {scrollBarFillYField?: string, visible?: boolean}) {
            let scrollbar = am5xy.XYChartScrollbar.new(root, {
                orientation: 'horizontal',
                height: 30,
                visible
            });
            chart.set('scrollbarX', scrollbar);
    
            let sbDateAxisRenderer = am5xy.AxisRendererX.new(root, {
                visible: false,
            });
    
            let sbDateAxis = scrollbar.chart.xAxes.push(MyDateAxis.new(root, {
                groupData: true,
                groupCount: 900,
    
                renderer: sbDateAxisRenderer,
                baseInterval: { timeUnit: 'second', count: 1 },
                tooltipDateFormat: 'ss',
                groupIntervals: [
                    { timeUnit: 'second', count: 1 },
                    { timeUnit: 'second', count: 2 },
                    { timeUnit: 'second', count: 5 },
                    { timeUnit: 'second', count: 10 },
                    { timeUnit: 'second', count: 15 },
                    { timeUnit: 'second', count: 30 },
                    { timeUnit: 'minute', count: 1 },
                    { timeUnit: 'minute', count: 2 },
                    { timeUnit: 'minute', count: 5 },
                    { timeUnit: 'minute', count: 10 },
                ],
                // tooltip: am5.Tooltip.new(root, {
                //     themeTags: ["axis"],
                // })
            }));
            sbDateAxisRenderer.grid.template.setAll({
                disabled: true,
                visible: false,
            });
            sbDateAxisRenderer.labels.template.setAll({
                disabled: true,
                visible: false,
            });

            let sbPowerAxisRenderer = am5xy.AxisRendererY.new(root, {
                ...axisConfig,
                visible: false,
            });
            sbPowerAxisRenderer.grid.template.setAll(axisGridConfig);
    
            let sbPowerAxis = scrollbar.chart.yAxes.push(am5xy.ValueAxis.new(root, {
                min: 0,
                strictMinMax: true,
                renderer: sbPowerAxisRenderer,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis']
                })
            }));
    
            let sbFillSeries = scrollbar.chart.series.push(MyLineSeries.new(root, {
                valueXField: startingAxisType === 'time' ? 'unix_time' : 'mm',
                valueYField: scrollBarFillYField,
    
                stroke: am5.color('#000000'),
    
                name: 'Power',
                yAxis: sbPowerAxis,
                xAxis: sbDateAxis,
            }));

            sbFillSeries.fills.template.setAll({
                fillOpacity: 1.0,
                visible: true,
            });

            return {
                sbDateAxis,
                sbFillSeries,
            }
        }
    
        return {
            dateAxis,
            createScrollbar,
            setMainDataSeries,
            setValidateData,
            getAxisType,
            toggleX,
        }
    }
    
    function createPowerAxis({root, chart, forceHidden = false}: XertAxis & {forceHidden?: boolean}) {
    
        // Power Axis
        let powerAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig
        });
        powerAxisRenderer.grid.template.setAll(axisGridConfig);
    
        let powerAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMaxSelection: true,
            extraMax: 0.01,
            renderer: powerAxisRenderer,
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis']
            }),
            forceHidden: forceHidden,
        }));
        powerAxis.ghostLabel.set('minWidth', 52);
    
        return powerAxis
    }
    
    function createSpeedAxis({root, chart, unit, usingPower = true, isSpeedInverted = false, startVisible: visible = true, forceHidden = false}: XertAxis & {unit: string, usingPower?: boolean, isSpeedInverted?: boolean, forceHidden?: boolean} ) {
        let speedAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(activityChartPalette.speed),
            opposite: usingPower,
            inversed: isSpeedInverted,
        });
        speedAxisRenderer.grid.template.setAll(axisGridConfig);
        let speedAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: isSpeedInverted ? undefined : 0,
            strictMinMax: true,
            visible: visible && (usingPower || isSpeedInverted),
            renderer: speedAxisRenderer,
            forceHidden: forceHidden,
            //minWidth: 120,
            // tooltipNumberFormat: `#.' ${speedUnit}'`,
            // TODO: FIX SPEED UNIT ITS MORE COMPLICATED (getSpeedUnitV4())
            // TODO: I don't think this tooltip is used anymore
            // tooltip: am5.Tooltip.new(root, {
            //     pointerOrientation: 'horizontal',
            //     labelText: '{name}: [bold]{' + (isSpeedInverted  ? 'valueY.formatDuration("m:ss")' : 'valueY.formatNumber("#.")') + '} '+ unit + '[/]',
            // }),
    
        }));
        if (isSpeedInverted)
            speedAxis.set("durationFormatter", am5.DurationFormatter.new(root, {
                // "numberFormat": "#,###.00"
                durationFormat: "m:ss"
            }));
        speedAxis.ghostLabel.set('minWidth', 36);
        if (!usingPower || isSpeedInverted) speedAxis.set('tooltip', am5.Tooltip.new(root, {
            themeTags: ['axis']
        }));
    
        return speedAxis
    }
    
    function createCursor({root, chart, handleChange}: XertChartRoot & {handleChange: (positionX: number) => void}) {
        let cursor = am5xy.XYCursor.new(root, {
            behavior: 'zoomX',
        })
        chart.set('cursor', cursor);
        const throttle = throttleTimeout(handleChange, 50);
        function handleChangeThrottled(positionX: number) {
            throttle.trigger(positionX);
        }
        cursor.events.on('cursormoved', (event) => {handleChangeThrottled(event.target.getPrivate('positionX'))})
    
        return cursor
    }

    function createAltitudeAxisSeries({root, chart, dateAxis, startingAxisType, unit, startVisible: visible = true, forceHidden = false}: XertAxisSeries & {unit: string, forceHidden?: boolean}) {
        const altitudeAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(activityChartPalette.altitude),
            opposite: true,
        });
        altitudeAxisRenderer.grid.template.setAll(axisGridConfig);

        const altitudeAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            visible,
            forceHidden,
            renderer: altitudeAxisRenderer,
            // tooltip: am5.Tooltip.new(root, { themeTags: ['axis'] }),
        }));
        
        const altitudeSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'alt',
            name: 'Altitude',
            stroke: am5.color(activityChartPalette.altitude),
            yAxis: altitudeAxis,
            xAxis: dateAxis,
            tooltip: ((tooltip)=> {
                tooltip.get('background').setAll({
                    fill: am5.color('#CEB1BE')
                });

                return tooltip
            }) ( am5.Tooltip.new(root, {
                getFillFromSprite: false,
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} '+unit+'[/]'
            })),
            visible,
            fill: am5.color('#FFFFFF'),
        }));
        altitudeSeries.strokes.template.setAll({
            opacity: 0.2,
            strokeWidth: 2.0,
        });
        altitudeSeries.fills.template.setAll({
            opacity: 0.1,
            visible: true,
            fillPattern: am5.LinePattern.new(root, {
                gap: 6,
                width: 79,
                height: 79,
                rotation: 45,
                color: am5.color(activityChartPalette.difficulty),
                strokeWidth: 2,
            })
        });
        altitudeSeries.on('visible', function(visible) {
            if (visible)
                altitudeAxis.show();
            else
                altitudeAxis.hide();
            altitudeSeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (!visible) altitudeSeries.get('tooltip')?.set('forceHidden', true);

        return {
            altitudeAxis,
            altitudeSeries,
        }
    }

    function createDifficultyAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: visible = true, forceHidden = false}: XertAxisSeries & {forceHidden?: boolean}) {
        const difficultyAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(activityChartPalette.difficulty),
            opposite: true,
        });
        difficultyAxisRenderer.grid.template.setAll(axisGridConfig);

        const difficultyAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            max: 200,
            strictMinMax: true,
            renderer: difficultyAxisRenderer,
            visible,
            forceHidden,
            // tooltip: am5.Tooltip.new(root, { themeTags: ['axis'] }),
        }));

        const difficultySeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
            // connect: false,

            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'xds',

            fill: am5.color('#FFFFFF'),
            name: 'Difficulty',
            // "simplifiedProcessing": true,
            stroke: am5.color(activityChartPalette.difficulty),
            ///"tensionX": 0.9,
            
            xAxis: dateAxis,
            yAxis: difficultyAxis,

            visible,


            tooltip: ((tooltip)=> {
                tooltip.get('background').setAll({
                    fill: am5.color('#2f7076')
                });


                return tooltip
            }) ( am5.Tooltip.new(root, {
                getFillFromSprite: false,
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")}[/]'
            })),
        }));
        difficultySeries.fills.template.setAll({
            opacity: 0.5,
            visible: true,
            fillPattern: am5.LinePattern.new(root, {
                gap: 3,
                width: 79,
                height: 79,
                //height: 5,
                rotation: 45,
                color: am5.color(activityChartPalette.difficulty),
                strokeWidth: 1,
                // strokeWidth: 0.5,
            })
        });
        difficultySeries.strokes.template.setAll({
            opacity: 0.4,
            strokeWidth: 2.0,
        });
        difficultySeries.on('visible', function(visible) {
            if (visible)
                difficultyAxis.show();
            else
                difficultyAxis.hide();
            difficultySeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (!visible) difficultySeries.get('tooltip')?.set('forceHidden', true);

        return {
            difficultyAxis,
            difficultySeries,
        }
    }

    function createCadenceAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: visible = true, forceHidden = false}: XertAxisSeries & {forceHidden?: boolean}) {
        const cadenceAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(activityChartPalette.cadence),
            opposite: true,
        });
        cadenceAxisRenderer.grid.template.setAll(axisGridConfig);
        const cadenceAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMax: true,
            // reverseChildren: true,
            visible,
            forceHidden,
            renderer: cadenceAxisRenderer,
            // tooltip: am5.Tooltip.new(root, { themeTags: ['axis'] }),
        }));
        const cadenceSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'cad',

            name: 'Cadence',

            stroke: am5.color(activityChartPalette.cadence),

            yAxis: cadenceAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} RPM[/]',
            }),
            visible,
        }));
        cadenceSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });

        cadenceSeries.on('visible', function(visible) {
            if (visible)
                cadenceAxis.show();
            else
                cadenceAxis.hide();
            cadenceSeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (!visible) cadenceSeries.get('tooltip')?.set('forceHidden', true);

        return {
            cadenceAxis,
            cadenceSeries,
        }
    }

    function createHeartRateAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: visible = true, forceHidden = false}: XertAxisSeries & {forceHidden?: boolean}) {
        const heartRateAxisRenderer = am5xy.AxisRendererY.new(root, {
            ...axisConfig,
            stroke: am5.color(activityChartPalette.heartRate),
            opposite: true,
        });
        heartRateAxisRenderer.grid.template.setAll(axisGridConfig);

        const heartRateAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            maxPrecision: 1,
            min: 0,
            strictMinMax: true,
            visible,
            forceHidden,
            renderer: heartRateAxisRenderer,
            // tooltip: am5.Tooltip.new(root, { themeTags: ['axis'] }),
        }));

        const heartRateSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'hr',

            name: 'Heart Rate',

            stroke: am5.color(activityChartPalette.heartRate),

            yAxis: heartRateAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} BPM[/]'
            }),
            visible,
        }));
        heartRateSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });
        heartRateSeries.on('visible', function(visible) {
            if (visible)
                heartRateAxis.show();
            else
                heartRateAxis.hide();
            heartRateSeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (!visible) heartRateSeries.get('tooltip')?.set('forceHidden', true);

        return {
            heartRateAxis,
            heartRateSeries,
        }
    }

    function createPowerFillSeries({root, chart, dateAxis, startingAxisType, axis, signature, startVisible = true, showIntervalDuration = false}: XertSeries & {signature: Signature, startVisible?: boolean, showIntervalDuration?: boolean}) {
        const gradientOpacity = 1.0
        let gradientStops = {}
        function resetGradientStops() {
            gradientStops = {}
        }

        const powerFillSeries = chart.series.push(MyLineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'power',
            name: 'Power',
            stroke: am5.color('#000000'),
            yAxis: axis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                // Workout charts carry the length of the interval each point belongs to, so the
                // hovered power is read together with how long it is held for.
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]'
                    + (showIntervalDuration ? '\nInterval: [bold]{interval_duration_str}[/]' : ''),
            }),
            visible: startVisible,
        }))
        if (!startVisible) powerFillSeries.get('tooltip')?.set('forceHidden', true)

        class MyGradient extends am5.LinearGradient {
            getFill(target) {              
                let dataItems = powerFillSeries.dataItems
                // let p0 = groupData ? dataItems[selectionMinGrouped] : dataItems[selectionMin];
                // let p1 = groupData ? dataItems[selectionMaxGrouped-1] : dataItems[selectionMax];
                if (!gradientStops[powerFillSeries._dataSetId]) {
                    let sig = signature;
                    let ftp = sig.ftp;
                    let ltp = sig.ltp ?? (sig.ftp - sig.atc/400);
                    let ltpMid = (ltp + ftp) /2;

                    // Re-calculate power colour to match re-sampled data.
                    let powerGradient = chroma.scale(['bbbbbb', '699cfe', '50e3c2', '79ff4c', 'ffef4c', 'ff7d2a', '#ff0c00'])
                    for (let item of powerFillSeries.dataItems) {
                        let v = item.dataContext;
                        // let mpa = v['mpa'];
                        let mpa = v['mmp0s'];
                        let mmp = v['mmpMid'];
                        let p = item._settings.valueY;
                        //let c = am5.Color.fromString(v["powerColor"]);
                        let rgba = powerGradient.domain([0, ltp, ltpMid, ftp, mmp, mpa])(p);

                        // the color property here isn't actually used by am5, but we read it ourselves later on
                        (item._settings as any).color = am5.Color.fromHex(rgba.num());

                    }

                    gradientStops[powerFillSeries._dataSetId] = true;
                }

                let min = Math.max(0, powerFillSeries.getPrivate('startIndex') - 1);
                let max = powerFillSeries.getPrivate('endIndex');
                // let p0 = groupData ? dataItems[selectionMinGrouped] : dataItems[min];
                // let p1 = groupData ? dataItems[selectionMaxGrouped-1] : dataItems[max-1];


                let bounds = this.getBounds(target)
                // let l = 0
                // let r = dateAxis.width()
                let l = bounds.left
                let r = bounds.right
                let w = r - l

                const gradient = this._root._renderer.createLinearGradient(l, 0, r, 0);
                // const stops = this.get("stops");

                // let start = dateAxis.get('start')
                // let end = dateAxis.get('end')

                // let x0 = p0._settings.point.x
                // let x1 = p1._settings.point.x - x0
                // console.log('getFill', {l, r})

                let opacity = gradientOpacity
                let colorPrev = null
                let prevOffset = 0.0
                for (let i = min; i < max; ++i) {
                    let item = dataItems[i];
                    if (!item || !item._settings.point) break;
                    let color = (item._settings as any).color
                    if (!color) break;
                    let offset = Math.max(0.0, Math.min(1.0, (item._settings.point.x - l) / w))
                    if (!colorPrev || colorPrev.r != color.r || colorPrev.g != color.g || colorPrev.b != color.b) {
                        if (colorPrev) {
                            gradient.addColorStop(prevOffset, 'rgba(' + colorPrev.r + ',' + colorPrev.g + ',' + colorPrev.b + ',' + opacity + ')')
                        }
                        gradient.addColorStop(offset, 'rgba(' + color.r + ',' + color.g + ',' + color.b + ',' + opacity + ')')
                    }
                    prevOffset = offset
                    colorPrev = color
                }

                return gradient;
            }
        }

        powerFillSeries.fills.template.setAll({
            // fillOpacity: 1.0,
            visible: true,

            fillOpacity: gradientOpacity,

            fillGradient: MyGradient.new(root, {
                // am5 documentation is wrong. Rotation not mentioned in IGradientSeries, but mentioned in FillGradient/LinearGradient
                // https://www.amcharts.com/docs/v5/reference/igradientsettings/
                // https://www.amcharts.com/docs/v5/concepts/colors-gradients-and-patterns/gradients/#Rotation
                // In practice, rotation does work
                //@ts-expect-error
                rotation: 0,
                // stops: stops
            })
        });

        return {
            powerFillSeries,
            resetGradientStops,
        }
    }

    function createMPASeries({root, chart, dateAxis, startingAxisType, startVisible: visible = true, axis}: XertSeries) {
        const MPASeries = chart.series.push(MySmoothedXLineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'mpa',
            name: 'MPA',
            stroke: am5.color('#CC33FF'), // TODO: MPA color gradient?
            yAxis: axis,
            xAxis: dateAxis,
            visible,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]\nXSSR: [bold]{xssr.formatNumber("#.")}[/]'
            }),
        }));
        MPASeries.strokes.template.setAll({
            strokeWidth: 3.0,
            lineJoin: 'round',
        });
        MPASeries.on('visible', function(visible) {
            MPASeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (!visible) MPASeries.get('tooltip')?.set('forceHidden', true);

        return MPASeries
    }

    function createThresholdPowerItem(powerAxis: AM5XY.ValueAxis<AM5XY.AxisRenderer>) {
        const thresholdPowerDataItem = powerAxis.makeDataItem({});
		powerAxis.createAxisRange(thresholdPowerDataItem);
		thresholdPowerDataItem.get('grid').setAll({
			stroke: am5.color(0xFF0000),
			strokeWidth: 1,
			strokeOpacity: 0.5,
			strokeDasharray: [3, 3],
			visible: true,
		});
		thresholdPowerDataItem.get('label').setAll({
			fill: am5.color(0xFF0000),
			text: 'Threshold Power',
			inside: true,
			centerX: 0,
			fontSize: 10,
			dy: -10,
		});

        return thresholdPowerDataItem
    }
    
    function createPeakPowerItem(powerAxis: AM5XY.ValueAxis<AM5XY.AxisRenderer>) {
        const peakPowerDataItem = powerAxis.makeDataItem({});
		powerAxis.createAxisRange(peakPowerDataItem);
		peakPowerDataItem.get('grid').setAll({
			stroke: am5.color(0x0000FF),
			strokeWidth: 1,
			strokeOpacity: 0.5,
			strokeDasharray: [3, 3],
			visible: true,
		});
		peakPowerDataItem.get('label').setAll({
			fill: am5.color(0x0000FF),
			text: 'Peak Power',
			inside: true,
			centerX: 0,
			fontSize: 10,
			dy: 10,
		});

        return peakPowerDataItem
    }

    /**
     * Hides the series axis when the series is deselected.
     * This is useful to avoid cluttering the chart with too many axes.
     */
    function hideSeriesAxisWhenDeselected({root, chart}: XertChartRoot) {

        // This needs to be done because adding any tooltip directly to the series axis
        // in addition to adding this event trigger will cause the event handler to overload. 
        // So this needs to run in order to make the tooltips visible the first time.

        chart.yAxes.each((axis)=>{
        
            axis.get('renderer').labels.template.set('minPosition', 0.05);

            if(axis.isVisible()) {
                axis.set('tooltip', am5.Tooltip.new(root, {
                    themeTags: ['axis']
                }));
            }

            axis.on('visible', (visible)=>{
                if (visible) {
                    axis.set('tooltip', am5.Tooltip.new(root, {
                        themeTags: ['axis']
                    }));
                } else {
                    axis.remove('tooltip');
                }

            });
        });
    }

    /**
     * This function returns a Promise that resolves when the chart has finished all actions.
     * Option to also run a callback function after the chart is ready.
     */
    function chartReady(root: am5Root, ms = 100, fn = () => {}): Promise<void> {
        return new Promise((resolve) => {
            const debounce = debouncer(ms);
            let chartActive = false;

            const handler = () => {
                chartActive = true
                debounce.debounce(() => {
                    root.events.off('frameended', handler);
                    fn();
                    resolve();
                });
            };

            root.events.on('frameended', handler);

            setTimeout(() => {
                if (chartActive) return; // Will eventually resolve in the handler
                root.events.off('frameended', handler);
                resolve();
            }, ms);
        });
    }

    function createSectionController<T extends LatLngData = LatLngData>(dateAxis: MyDateAxis, mainAxis: AM5XY.ValueAxis<AM5XY.AxisRenderer>, startingData: T[], startingAxisType: ActivityAxisType, mapController: MapController = noopMapController): SectionController<T> {
        const sectionRange = dateAxis.createAxisRange(dateAxis.makeDataItem({}));
        const sectionRangeEnd = dateAxis.createAxisRange(dateAxis.makeDataItem({}));
        let segmentGrid = {
            stroke: am5.color(0x000000),
            strokeWidth: 1,
            strokeOpacity: 0.5,
            strokeDasharray: [3, 3],
            fill: am5.color(0x000000),
            fillOpacity: 0.5,
            visible: true,
            location: 0.5,
        };
        sectionRange.get('grid').setAll(segmentGrid);
        sectionRangeEnd.get('grid').setAll(segmentGrid);
        sectionRange.get('label').setAll({
            fill: am5.color(0x333333),
            inside: true,
            rotation: -90,
            dx: -20,
            dy: 200 - mainAxis.height(), // TODO: Make sure this makes sense with final chart height
            // centerX: am5.p0,
        })

        let axis = startingAxisType
        let data = startingData
        let sectionData: SectionInterface & {startData: T, endData: T }

        function showSection({startTime, elapsedTime, name = null}: SectionInterface) {
            // Currently this only finds the section based on time even if using the distance axis.
            let cmp = (a, b)=> { return a.unix_time - b.unix_time };
            let arg = { unix_time: startTime }
            let i0 = bounds.eq(data, arg, cmp);
            arg.unix_time += elapsedTime;
            let i1 = bounds.eq(data, arg, cmp, i0);
            let startIndex = Math.max(0, i0);
            let endIndex = Math.min(data.length - 1, i1);

            sectionData = {
                startTime,
                startData: data[startIndex],
                endData: data[endIndex],
                elapsedTime,
                name,
            }

            if(!mapController.noop) {
                let path = [];
                for (let i = startIndex; i < endIndex; i++) {
                    const p = data[i];
                    if (p.lat && p.lng)
                        path.push({lat: p.lat, lng: p.lng});
                }
                if (path.length > 0) {
                    mapController.drawSectionLine(path)
                }
            }

            setSection()
            return {startData: data[startIndex], endData: data[endIndex], startIndex, endIndex}
        }

        function setSection() {
            if (!sectionData) return
            const startData = sectionData.startData
            const endData = sectionData.endData
            const isTime = axis === "time"
            const xStart = isTime ? startData.unix_time ?? 0 : startData.mm ?? 0
            const xEnd = isTime ? endData.unix_time : endData.mm

            sectionRange.set('value', xStart);
            sectionRange.set('endValue', xStart+1000);
            if (sectionData.name) sectionRange.get('label').set('text', sectionData.name);
            sectionRangeEnd.set('value', xEnd);
            sectionRangeEnd.set('endValue', xEnd+1000);
        }

        function removeSection() {
            sectionRange.set('value', null);
            sectionRange.set('endValue', null);
            sectionRange.get('label').set('text', '');

            sectionRangeEnd.set('value', null);
            sectionRangeEnd.set('endValue', null);

            sectionData = null
            mapController.removeSectionLine()
        }

        function switchAxis(axisType: ActivityAxisType, newData: T[]) {
            data = newData
            axis = axisType
            // Recompute indexes because they are old now
            if(sectionData) showSection({
                startTime: sectionData.startTime,
                elapsedTime: sectionData.elapsedTime,
                name: sectionData.name,
            })
        }

        async function zoomToSection() {
            if(!sectionData) return
            const startData = sectionData.startData
            const endData = sectionData.endData
            const isTime = axis === "time"
            const xStart = isTime ? startData.unix_time : startData.mm
            const xEnd = isTime ? endData.unix_time : endData.mm

            dateAxis.zoomToValues(xStart, xEnd, 0);

            await chartReady(dateAxis.root)
            return
        }
        
        return {
            showSection,
            removeSection,
            switchAxis,
            zoomToSection,
        }
    }

    function createStravaSegmentAnalysisController<T extends LatLngData = LatLngData>(root: am5Root, chart: am5xyChart, legend: AM5.Legend, dateAxis: MyDateAxis, powerAxis: AM5XY.ValueAxis<AM5XY.AxisRenderer>, startData: T[], startingAxisType: ActivityAxisType, map: MapControllerInterface = {controller: noopMapController, group: null}): StravaSegmentController<T> {
        const mapGroup = `${map?.group}-strava`
        const powerConfig = {
            name: 'Breakthrough Effort',
            xAxis: dateAxis,
            yAxis: powerAxis,
            valueXField: startingAxisType === "time" ? 'unix_time' : 'mm',
            valueYField: 'power'
        }
        const fills = { fillOpacity: 0.5, visible: true }
        const strokes = { strokeWidth: 2 }

        const estimatedEffortSeries = chart.series.push(am5xy.LineSeries.new(root, {
            ...powerConfig, fill: am5.color(0x33AA33), stroke: am5.color('#000000'),
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
            }),
        }));
        estimatedEffortSeries.fills.template.setAll(fills);
        estimatedEffortSeries.strokes.template.setAll(strokes);

        const estimatedMPASeries = chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
            ...powerConfig, stroke: am5.color(0xFF33AA), valueYField: 'mpa', name: 'Breakthrough MPA',
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
            }),
        }));
        estimatedMPASeries.strokes.template.setAll(strokes);

        const bestEffortSeries = chart.series.push(am5xy.LineSeries.new(root, {
            ...powerConfig, name: 'Current PR', fill: am5.color(0xFFAA33), stroke: am5.color('#000000'),
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]',
            }),
        }));
        bestEffortSeries.fills.template.setAll(fills);
        bestEffortSeries.strokes.template.setAll(strokes);
        
        let data = startData
        const segmentController = createSectionController(dateAxis, powerAxis, data, startingAxisType, map.controller)
        let setSeries: () => void

        map.controller.addMarker({
            id: "prEffort",
            lat: data[0].lat,
            lng: data[0].lng,
            image: iconDiamondOrange,
            group: mapGroup,
            hidden: true,
        });
        map.controller.addMarker({
            id: "btEffort",
            lat: data[0].lat,
            lng: data[0].lng,
            image: iconDiamond,
            group: mapGroup,
            hidden: true,
        });
        

        function runStravaSegmentAnalysis(segment: StravaSegmentInfo & {startTime: number, elapsedTime: number}) {
            removeStravaSegmentAnalysis() // Resets segments and legend
            const startData = segmentController.showSection({name: segment.name, startTime: segment.startTime, elapsedTime: segment.elapsedTime}).startData
            let activity_d0 = startData.mm
            let activity_t0 = startData.unix_time;
            let tBestCurrent = null;
            let tBestPR = null;
            // let estBestPRPowerString = "N/A";
            let estBestCurrentPowerString = 'N/A';
            let prPowerString = 'N/A';
            if ('effort_streams' in segment && 'watts' in segment.effort_streams) {
                let dist;
                let alt;

                let curr_streams = segment.effort_streams;
                let curr_ps = curr_streams.watts.data;
                let curr_ts = curr_streams.time.data;
                let curr_dists = curr_streams.distance.data;
                let curr_latlngs = curr_streams.latlng.data;

                let curr_t0 = curr_ts[0];

                // 5-second moving average for Power.
                // TODO: Use power-avg-control only if available.
                let n_avg = 5; //+$('#power-avg-control').val();

                let currentEffortData = [];
                if (curr_dists.length > 0) {
                    let d0 = curr_dists[0];
                    let n = curr_ps.length;
                    let p_avg = 0;
                    let avgs = [];
                    for (let i = 0; i < n; ++i) {
                        let latlng = curr_latlngs[i];

                        let p = curr_ps[i];
                        p_avg += p;

                        avgs.push(p);
                        if (avgs.length > n_avg) {
                            p_avg -= avgs.shift();
                        }
                        p = p_avg / avgs.length;
                        currentEffortData.push({
                            // t: (curr_ts[i] - curr_t0)*1000,
                            unix_time: (curr_ts[i])*1000,
                            // mm: (curr_dists[i] - d0)*1000,
                            mm: activity_d0 + (curr_dists[i] - d0)*1000,
                            power: p,
                            lat: latlng[0],
                            lng: latlng[1]
                        });
                    }
                }


                let bestEffortData = [];
                let pr_effort = segment.pr_effort;
                if (pr_effort != null && pr_effort.streams.watts != null) {
                    let pr_streams = pr_effort.streams;
                    let pr_ps = pr_streams.watts.data;
                    let pr_alts = pr_streams.altitude.data;
                    let pr_latlngs = pr_streams.latlng.data;
                    let pr_dists = pr_streams.distance.data;
                    let pr_ts = pr_streams.time.data;

                    let t0 = pr_ts[0];


                    let n = pr_alts.length;
                    let d0 = pr_dists[0];
                    let avgs = [];
                    let p_avg = 0;

                    let p_avg_pr = 0;
                    for(let i = 0; i < n; ++i) {
                        dist = pr_dists[i];
                        alt = pr_alts[i];

                        let p = pr_ps[i];
                        p_avg_pr += p;
                        p_avg += p;

                        avgs.push(p);
                        if (avgs.length > n_avg) {
                            p_avg -= avgs.shift();
                        }

                        p = p_avg / avgs.length;


                        let latlng = pr_latlngs[i];
                        bestEffortData.push({
                            // t: (pr_ts[i]-t0)*1000,
                            unix_time: activity_t0 + (pr_ts[i]-t0)*1000,
                            mm: activity_d0 + (dist-d0)*1000,
                            power: p, lat: latlng[0], lng: latlng[1]
                        });
                        
                    }
                }

                let data = [];
                let p_avg = 0;
                if (segment.estimate_current && segment.estimate_current.records && segment.estimate_current.records.length > 0) {
                    let estimate_records = segment.estimate_current.records;
                    // let p_scale = segment.pr_estimate_scale;
                    // let sig = Xert.Activity.Analytics.MPA._data.sig;

                    let n = estimate_records.length;
                    for(let i = 0; i < n; ++i) {

                        let estimate = estimate_records[i];

                        p_avg += estimate.power;
                        data.push({
                            // t: (pr_ts[i]-t0)*1000,
                            unix_time: activity_t0 + estimate.t_scaled*1000,
                            mm: activity_d0 + estimate.mm,
                            power: estimate.power, lat: estimate.lat, lng: estimate.lng,
                            mpa: estimate.mpa,
                        });
                    }
                }

                let scaledCurrentEffortData = {data: data, p_avg: data.length > 0 ? p_avg / data.length : 0 }

                estBestCurrentPowerString = scaledCurrentEffortData.p_avg.toFixed(0) + ' W';
                // estBestPRPowerString = scaledPREffortData.p_avg.toFixed(0) + " W";
                // if (n > 0)
                //     prPowerString = (p_avg_pr / n).toFixed(0) + " W";
                if (pr_effort && pr_effort.average_watts)
                    prPowerString = pr_effort.average_watts + ' W';

                tBestCurrent = segment.estimate_current ? segment.estimate_current.duration : null;
                // tBestPR = segment.estimate_pr ? segment.estimate_pr.duration : null; // TODO: estimate_pr not used ??
                if (tBestCurrent <= 0) tBestCurrent = null;
                if (tBestPR <= 0) tBestPR = null;

                function _setSeries () { // Create callback. Easier than saving/typing the data.
                    bestEffortSeries.data.setAll(bestEffortData);
                    estimatedEffortSeries.data.setAll(scaledCurrentEffortData.data);
                    estimatedMPASeries.data.setAll(scaledCurrentEffortData.data);
                }
                setSeries = _setSeries
                setSeries()

                // Update markers
                map.controller.setMarkerPosition('prEffort', bestEffortData[0].lat, bestEffortData[0].lng, mapGroup);
                map.controller.setMarkerPosition('btEffort', scaledCurrentEffortData.data[0].lat, scaledCurrentEffortData.data[0].lat, mapGroup);
                map.controller.showMarker('prEffort', mapGroup);
                map.controller.showMarker('btEffort', mapGroup);

                // Set the legend
                legend.data.pushAll([bestEffortSeries, estimatedEffortSeries, estimatedMPASeries])
                // TODO: Only keep certain series enabled.
            }            
        }

        function switchAxis(axisType: ActivityAxisType, newData: T[]) {
            data = newData
            segmentController.switchAxis(axisType, newData)
            if(setSeries) setSeries() // Runs set all again assuming the valueXField has been already switched
        }

        function removeStravaSegmentAnalysis() {
            setSeries = null
            bestEffortSeries.data.clear()
            estimatedEffortSeries.data.clear()
            estimatedMPASeries.data.clear()
            segmentController.removeSection()
            legend.data.setAll(
                legend.data.values.filter((item) => item !== bestEffortSeries && item !== estimatedEffortSeries && item !== estimatedMPASeries)
            )
            map.controller.hideMarker('btEffort', mapGroup)
            map.controller.hideMarker('prEffort', mapGroup)
        }

        function updateMarkers(position: number) {
            if(!setSeries) return; // No segment analysis has been run yet

            const prData = dateAxis.getSeriesItem(bestEffortSeries, position)?.dataContext
            const btData = dateAxis.getSeriesItem(estimatedEffortSeries, position)?.dataContext
            if (prData) {
                map.controller.setMarkerPosition('prEffort', prData.lat, prData.lng, mapGroup);
            }
            if (btData) {
                map.controller.setMarkerPosition('btEffort', btData.lat, btData.lng, mapGroup);
            }
        }

        return {
            ...segmentController,
            switchAxis,
            runStravaSegmentAnalysis,
            removeStravaSegmentAnalysis,
            updateMarkers,
            bestEffortSeries,
            estimatedEffortSeries,
            estimatedMPASeries
        }

    }

    return {
        // AM5
        am5, am5xy, am5plugins_exporting,
        // Xert AM5 Extensions
        MyTheme, MyLineSeries, 
        // Assign Data
        fastGroupData,
        // Create axis/series
        createMyDateAxis, createPowerAxis, createSpeedAxis, createCursor, chartReady,
        createAltitudeAxisSeries, createDifficultyAxisSeries, createCadenceAxisSeries, createHeartRateAxisSeries, hideSeriesAxisWhenDeselected,
        createPowerFillSeries, createMPASeries, createThresholdPowerItem, createPeakPowerItem, createSectionController, createStravaSegmentAnalysisController
    }
}

export function createChartController<T extends SectionController = SectionController>(
    sectionController: T, 
    axisType: Writable<ActivityAxisType>, 
    switchAxis: (axisType: ActivityAxisType) => Promise<void>, 
    switchPowerType: (powerType: PowerType) => void,
    chartReady: Promise<void>)
{
    return {
        getAxisType: () => get(axisType),
        sectionController,
        switchAxis,
        switchPowerType,
        chartReady,
    }
}

export type ChartController<T extends SectionController = SectionController> = ReturnType<typeof createChartController<T>>

export function createEmptyChartController<T extends SectionController = SectionController>(): ChartController<T> {
    return {
        getAxisType: () => null,
        sectionController: null,
        switchAxis: null,
        switchPowerType: null,
        chartReady: new Promise(() => {})
    }
}

export interface SectionInterface {
    startTime: number, 
    elapsedTime: number, 
    name?: string
}

export function getSectionInterfaceFromXertSegment (segment: XertSegment): SectionInterface {
    return {startTime: segment.start_time*1000, elapsedTime: segment.elapsed_time*1000, name: segment.name}
}

export interface SectionController<T extends LatLngData = LatLngData>  {
    showSection: (segmentInterface: SectionInterface) => {startData: T, endData: T, startIndex: number, endIndex: number},
    removeSection: () => void,
    switchAxis: (axisType: ActivityAxisType, newData: T[]) => void,
    zoomToSection: () => Promise<void>,
}

export interface StravaSegmentController<T extends LatLngData = LatLngData> extends SectionController<T> {
    runStravaSegmentAnalysis: (segment: StravaSegmentInfo & {startTime: number, elapsedTime: number}) => void,
    removeStravaSegmentAnalysis: () => void,
    updateMarkers: (position: number) => void,
    bestEffortSeries: AM5XY.LineSeries,
    estimatedEffortSeries: AM5XY. LineSeries,
    estimatedMPASeries: AM5XY. LineSeries
}

export type ActivityChartSummary = {
    adu: number
    ahp: number
    alp: number
    ampa: number
    ap: number
    app: number
    aps: number
    asr: number
    bem: number
    bep: number
    fd: number
    final_hie: number
    final_tp: number
    fp: number
    hiw: number
    hsr: number
    hws: number
    liw: number
    lsr: number
    lws: number
    mep: number
    mes: number
    moving_time: {
        adu: number
        ahp: number
        alp: number
        ampa: number
        ap: number
        app: number
        aps: number
        asr: number
        hsr: number
        lsr: number
        np: number
        psr: number
        tss: number
    }
    mps: number
    np: number
    piw: number
    psr: number
    pws: number
    sfd: number
    sfp: number
    sp: number
    tgc: number
    tgf: number
    tss: number
    tw: number
    tws: number
    xds: number
    xep: number
    xhss: number
    xlss: number
    xpss: number
    xss: number
}

export type ProcessedDataPoint = {
    alt: number,
    cad: number,
    dist: number,
    hr: number,
    hws: number,
    lat: number,
    lng: number,
    lws: number,
    mm: number,
    mmp0s: number,
    mmpMid: number,
    mpa: number,
    p_raw: number,
    power: number,
    pws: number,
    spd: number,
    tgc: number,
    tgf: number,
    tgt: number,
    timestamp: number,
    tws: number,
    unix_time: number,
    xds: number,
    xssr: number,
    bt_overage_power?: number,
    overage_power?: number,
    /** Workout charts only: length of the interval this point belongs to, formatted for the tooltip. */
    interval_duration_str?: string,
}

export async function processRecordsForChart(data: ActivityRecordsData, fitnessSignature: Signature & { carb_bias: number, }, isMetric: boolean, chartAvgOverSecs: number) {
    let n_avg = chartAvgOverSecs;
    let avg_mpa = false;
    let n_mmp = 0;

    console.log('====> Running updateAvg!');
    let start = new Date();
    let processedData: ProcessedDataPoint[] = [];
    let metrics: ActivityChartSummary;
    let min: ProcessedDataPoint & { index: number; min_overage_power: number; min_overage_mpa: number; };


    // TODO: Pass use_moving_time param from calendar settings.
    let out = (await getLibxert()).analyze_activity(data.time, data.dist, data.lat, data.lng, data.spd, data.cad, data.power, n_avg, n_mmp, fitnessSignature.pp, fitnessSignature.ftp, fitnessSignature.atc, fitnessSignature.carb_bias, isMetric, false, 0.0, "", 0.0);
    let count = out.ps.length;
    let end_wasm = new Date();

    const distConversion = isMetric ? 1.0 : kmToMile(1);
    const elevConversion = isMetric ? 1.0 : meterToFoot(1);

    let consec = 0;
    let maxconsec = 30;
    let k_prev = 0;
    for (let i = 0; i < count; ++i) {
        const k = out.indices[i];
        if (k < data.hr.length - 1 && data.hr[k] === data.hr[k + 1] || k === k_prev) consec++;
        else consec = 0;
        if (consec > maxconsec) {
            data.hr[k] = null;
        }
        k_prev = k;

        processedData.push({
            timestamp: data.time[k] / 1000.0, // TOOD: Use unix_time + t0?
            unix_time: out.ts[i],
            mm: out.mms[i],
            power: out.ps[i],
            mpa: out.mpas[i],
            mmp0s: out.mmp0s[i],
            mmpMid: out.mmpMid[i],

            tgf: out.fats[i],
            tgc: out.carbs[i],

            tws: out.tws[i],

            lws: out.lws[i], // Needed for segment focus calc.
            hws: out.hws[i],
            pws: out.pws[i],

            xds: out.xds[i],
            xssr: out.xssr[i],

            p_raw: data.power[k],
            hr: data.hr[k],
            cad: data.cad[k],
            // spd: isRunningActivity ? (data.spd[k] > 8 ? 3600.0 / (data.spd[k]*distConversion) : undefined) : data.spd[k]*distConversion, // ! Original with running
            spd: false ? (data.spd[k] > 8 ? 3600.0 / (data.spd[k] * distConversion) : undefined) : data.spd[k] * distConversion,
            dist: data.dist[k] * distConversion,
            alt: data.alt[k] * elevConversion,
            lat: data.lat[k],
            lng: data.lng[k],
            tgt: data.tgt[k],
            overage_power: (out.ps[i] && out.mpas[i] && out.mpas[i] - out.ps[i] < 0) ? out.ps[i] : undefined,
        });
    }
    if (out.i_best_effort > 0) {
        const minDataPoint = processedData[out.i_best_effort];
        min = {
            ...minDataPoint,
            index: out.i_best_effort,
            min_overage_mpa: minDataPoint["mpa"],
            min_overage_power: minDataPoint["power"]
        };
        // processedData[out.i_best_effort] = min; // TODO: Don't think this is required
    }


    metrics = out.metrics;
    // metrics['fa'] = this.getFocus(metrics['sfd']); // ? Not sure if this is used at the moment 
    let end_js = new Date();
    console.log('wasm took ' + (end_wasm.getTime() - start.getTime()) / 1000.0 + ' + ' + (end_js.getTime() - end_wasm.getTime()) / 1000.0 + ' (' + (end_js.getTime() - start.getTime()) / 1000.0 + ')');

    // Reconstruct mpaData from rawData.
    return {
        data: processedData,
        min: min,
        signature: fitnessSignature,
        summary: metrics, // TODO: Not sure what purpose this holds yet
        // TODO: Pretty sure these are legacy from when it used to be a get request
        // result: true,
        // msg: '',
        
        // events: [],
    };
}

export type ActivityChartMpaDataPromise = ReturnType<typeof processRecordsForChart>
export type ActivityChartMpaData = Awaited<ReturnType<typeof processRecordsForChart>>

export async function getSharedActivityPowerAverage(
    shared: SharedActivity,
    signature: Signature,
    movingAverage: number = 5
): Promise<SharedActivityMarked> {
    const libxert = await getLibxert();
    const power = shared.records.map(r => r.power);
    const time = shared.records.map(r => r.unix_time);
    const relativePower = shared.records.map(r => r.rp);
    const powerMA = libxert.power_moving_average(time, power, movingAverage, signature.pp);
    const rpMA = libxert.power_moving_average(time, relativePower, movingAverage, signature.pp);
    return {
        ...shared,
        records: shared.records.map((point, idx) => ({
            ...point,
            power: powerMA[idx],
            rp: rpMA[idx]
        })),
        dirty: true,
    };
}

export async function getSharedActivitiesPowerAverage(
    sharedActivities: Record<string, SharedActivity>,
    signature: Signature,
    movingAverage: number = 5
): Promise<Record<string, SharedActivityMarked>> {
    let sharedActivitiesPowerAverage: Record<string, SharedActivityMarked> = {};
    for (const key in sharedActivities) {
        sharedActivitiesPowerAverage[key] = await getSharedActivityPowerAverage(sharedActivities[key], signature, movingAverage);
    }
    return sharedActivitiesPowerAverage;
}

export function getGroupMetrics(
    useMovingTime: string,
    records: SharedActivityRecord[],
    userWeight: number,
    startTime: number,
    endTime: number
) {
    if (useMovingTime === "mov" || useMovingTime === "pedal") {
        records = records.filter(r => r.moving === true);
    }

    // Use binary-search-bounds to find start and end indices
    const cmp = (a, b) => a.unix_time - b.unix_time;
    const startIndex = bounds.ge(records, { unix_time: startTime }, cmp);
    const endIndex = bounds.le(records, { unix_time: endTime }, cmp, startIndex);

    const sliced = records.slice(startIndex, endIndex + 1);

    const rpSum = sliced.reduce((sum, r) => sum + (r.rp || 0), 0);
    const powerSum = sliced.reduce((sum, r) => sum + (r.power || 0), 0);
    const speedSum = sliced.reduce((sum, r) => sum + (r.spd || 0), 0);
    const count = sliced.length || 1;
    const rpAvg = rpSum / count;
    const powerAvg = powerSum / count;
    const speedAvg = speedSum / count;
    const wkg = userWeight ? powerAvg / userWeight : 0;
    return { rpAvg, powerAvg, wkg, speedAvg };
}